import { Button, Card, Link, Stack, Typography, TextField, IconButton, useTheme } from "@mui/material";
import { alignProperty } from "@mui/material/styles/cssUtils";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { MdSettingsInputAntenna } from "react-icons/md";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { getPosts, getUserLikedPosts } from "../api/posts";
import { isLoggedIn } from "../helpers/authHelper";
import CreatePost from "./CreatePost";
import Loading from "./Loading";
import PostCard from "./PostCard";
import SortBySelect from "./SortBySelect";
import HorizontalStack from "./util/HorizontalStack";
import UserAvatar from "./UserAvatar";
import { Image as ImageIcon, Link as LinkIcon, EmojiEmotions as EmojiIcon, Send as SendIcon } from "@mui/icons-material";

const PostBrowser = (props) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [end, setEnd] = useState(false);
  const [sortBy, setSortBy] = useState("-createdAt");
  const [count, setCount] = useState(0);
  const user = isLoggedIn();

  const [search] = useSearchParams();
  const [effect, setEffect] = useState(false);

  const searchExists =
    search && search.get("search") && search.get("search").length > 0;

  const fetchPosts = async () => {
    setLoading(true);
    const newPage = page + 1;
    setPage(newPage);

    let query = {
      page: newPage,
      sortBy,
    };

    let data;

    if (props.contentType === "posts") {
      if (props.profileUser) query.author = props.profileUser.username;
      if (searchExists) query.search = search.get("search");

      data = await getPosts(user && user.token, query);
    } else if (props.contentType === "liked") {
      data = await getUserLikedPosts(
        props.profileUser._id,
        user && user.token,
        query
      );
    }

    if (data.data.length < 10) {
      setEnd(true);
    }

    setLoading(false);
    if (!data.error) {
      setPosts([...posts, ...data.data]);
      setCount(data.count);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [sortBy, effect]);

  useEffect(() => {
    setPosts([]);
    setPage(0);
    setEnd(false);
    setEffect(!effect);
  }, [search]);

  const handleSortBy = (e) => {
    const newSortName = e.target.value;
    let newSortBy;

    Object.keys(sorts).forEach((sortName) => {
      if (sorts[sortName] === newSortName) newSortBy = sortName;
    });

    setPosts([]);
    setPage(0);
    setEnd(false);
    setSortBy(newSortBy);
  };

  const removePost = (removedPost) => {
    setPosts(posts.filter((post) => post._id !== removedPost._id));
  };

  const handleBackToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const contentTypeSorts = {
    posts: {
      "-createdAt": "Latest",
      "-likeCount": "Likes",
      "-commentCount": "Comments",
      createdAt: "Earliest",
    },
    liked: {
      "-createdAt": "Latest",
      createdAt: "Earliest",
    },
  };

  const sorts = contentTypeSorts[props.contentType];
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <>
      <Stack spacing={3}>
        {/* Post Composer Card */}
        {props.createPost && user && (
          <Card
            sx={{
              p: 2.5,
              borderRadius: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              backgroundColor: "white",
            }}
          >
            <HorizontalStack spacing={2} alignItems="flex-start">
              <UserAvatar username={user.username} width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Share something with the community..."
                  variant="outlined"
                  onClick={() => navigate("/posts/create")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "rgba(0,0,0,0.02)",
                      "& fieldset": {
                        borderColor: "rgba(0,0,0,0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(124,58,237,0.2)",
                      },
                    },
                  }}
                  InputProps={{
                    readOnly: true,
                    sx: { cursor: "pointer" },
                  }}
                />
                <HorizontalStack justifyContent="space-between" sx={{ mt: 1.5 }}>
                  <HorizontalStack spacing={1}>
                    <IconButton
                      size="small"
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: theme.palette.primary.main },
                      }}
                    >
                      <ImageIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: theme.palette.primary.main },
                      }}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: theme.palette.primary.main },
                      }}
                    >
                      <EmojiIcon fontSize="small" />
                    </IconButton>
                  </HorizontalStack>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/posts/create")}
                    startIcon={<SendIcon />}
                    sx={{
                      borderRadius: "12px",
                      px: 2.5,
                      textTransform: "none",
                      fontWeight: 600,
                      background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
                      boxShadow: "0 3px 10px rgba(124,58,237,0.25)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #6D28D9 0%, #9333EA 100%)",
                        boxShadow: "0 5px 14px rgba(124,58,237,0.35)",
                      },
                    }}
                  >
                    Post
                  </Button>
                </HorizontalStack>
              </Box>
            </HorizontalStack>
          </Card>
        )}

        {/* Show login/signup prompt when not authenticated and not loading */}
        {!loading && !user && (
          <Card
            sx={{
              p: 4,
              borderRadius: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              backgroundColor: "white",
              textAlign: "center",
            }}
          >
            <Stack spacing={3} alignItems="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "2.5rem",
                }}
              >
                💬
              </Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                Join ChatLog to start sharing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in or create an account to post, comment, and connect with others
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/login")}
                  sx={{
                    borderRadius: "12px",
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#7C3AED",
                    color: "#7C3AED",
                    "&:hover": {
                      borderColor: "#6D28D9",
                      backgroundColor: "rgba(124,58,237,0.1)",
                    },
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate("/signup")}
                  sx={{
                    borderRadius: "12px",
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
                    boxShadow: "0 4px 12px rgba(124,58,237,0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #6D28D9 0%, #9333EA 100%)",
                      boxShadow: "0 6px 16px rgba(124,58,237,0.4)",
                    },
                  }}
                >
                  Sign Up
                </Button>
              </Stack>
            </Stack>
          </Card>
        )}

        {/* Latest Posts Section */}
        {user && (
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "text.primary",
              }}
            >
              Latest Posts
            </Typography>
          </Box>
        )}

        {searchExists && (
          <Box>
            <Typography variant="h5" gutterBottom>
              Showing results for "{search.get("search")}"
            </Typography>
            <Typography color="text.secondary" variant="span">
              {count} results found
            </Typography>
          </Box>
        )}

        {posts.map((post, i) => (
          <PostCard
            preview="primary"
            key={post._id}
            post={post}
            removePost={removePost}
          />
        ))}

        {loading && <Loading />}
        {end ? (
          <Stack py={5} alignItems="center">
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {posts.length > 0 ? (
                <>All posts have been viewed</>
              ) : (
                <>No posts available</>
              )}
            </Typography>
            <Button variant="text" size="small" onClick={handleBackToTop}>
              Back to top
            </Button>
          </Stack>
        ) : (
          !loading &&
          posts &&
          posts.length > 0 && (
            <Stack pt={2} pb={6} alignItems="center" spacing={2}>
              <Button
                onClick={fetchPosts}
                variant="text"
                sx={{
                  textTransform: "none",
                  color: "text.secondary",
                  fontWeight: 500,
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: "transparent",
                  },
                }}
              >
                Load More &gt;
              </Button>
            </Stack>
          )
        )}
      </Stack>
    </>
  );
};

export default PostBrowser;
